<template>
  <div>
    <h1 class="mb-30">Аналитика</h1>

    <VSelect
        label="Тип отчета:"
        v-model="selectedReport"
        class="mb-30"
        width="400px"
        :allow-empty="false"
        :options="reportTypeList"
    />

    <AnalyticsFilter
      :filters="filters"
      :is-loading="isLoading"
      :selected-report="selectedReport"
      @submit="filterSubmit"
      ref="analyticsFilter"
    />

    <Spinner v-if="isLoading"/>

    <div v-else-if="items.length">
      <div v-if="hasChart" class="d-flex mb-30 gap-l">
        <VCheckbox
          v-model="isTable"
          label="Таблица"
          :disabled="!hasChart"
        />

        <VCheckbox
          v-model="isChart"
          label="График"
        />
      </div>

      <div class="between ai-c mb-20">
        <h3>{{ selectedReport.name }}</h3>

        <div class="buttons">
          <VButton
            text="На печать"
            icon="print"
            size="sm"
            @click.native="openModal('print')"
          />

          <download-excel
            v-if="isTable"
            :name="selectedReport.name"
            :header="excelHeader"
            :fields="excelFields"
            :fetch="getExcelData"
          >
            <VButton
              text="Экспорт в XLS"
              icon="export"
              size="sm"
              :loading="isLoadingExcel"
            />
          </download-excel>
        </div>
      </div>

      <!--  Table  -->
      <div v-if="isTable" id="printTable" class="mb-30">
        <ReportFilterInfo
          v-if="visibleReportFilterInfo"
          class="absolute hidden mb-30"
          :title="selectedReport.name"
          :date="reportFilter.date"
          :filters="reportFilter.filters"
        />

        <ReclamationInnerTable
          v-if="isReportReclamationTypeCustomTable"
          :rows="items"
          :columns-report="columns"
        />

        <AppTable
          v-else
          ref="table"
          :class="selectedReport.table.classes"
          :columns-before="selectedReport.table.columnsBefore || []"
          :columns="columns"
          :handler-columns="selectedReport.table.handlerColumns"
          :items="items"
          :tree-children-keys="selectedReport.table.treeKeys"
          :is-tree-table="selectedReport.table.isTreeTable"
          :is-open-tree="isOpenTableTree"
          :row-active="selectedReport.table.isRowActive"
          :is-sticky="!!selectedReport.table.isSticky"
          @rowClick="onTableRow"
          @sorted="sortedHandler"
          withTotal
        />
      </div>

      <!--  Chart  -->
      <div v-if="isChart" id="printChart">
        <ReportFilterInfo
          v-if="visibleReportFilterInfo"
          class="mb-30"
          :title="selectedReport.name"
          :date="reportFilter.date"
          :filters="reportFilter.filters"
        />

        <RadioGroup
          v-if="visibleChartRadio"
          class="radio-group--row mb-20"
          label="Выберите тип графика"
          :options="currentReport.chartRadioOptions"
          :value="activeChartRadio"
          @input="changeActiveChartRadio"
        />

        <BarChart
          chart-id="chart_bar"
          class="mb-30"
          :chartData="chartData"
        />
      </div>
    </div>

  <h3 v-else-if="isNotFound">Ничего не найдено</h3>

  <ModalPrint
    v-if="isModal === 'print'"
    :print-options="printOptions"
    init-print-type="table"
    @request="toPrint"
    @reject="closeModal"
  />

  <!-- неизвестная модалка отключена, была по клику на строку в таблице @rowClick="onTableRow" -->
  <BaseModal
    v-if="isModal === 'table'"
    :title="selectedReport.modal ? selectedReport.modal.title : ''"
    class="modal-changelog"
    @close="closeModalTable"
  >
      <AppTable
        class="modal__table modal__box custom-scroll"
        :columns="selectedReport.modal.columns"
        :items="modalTable.items"
      />
    </BaseModal>
  </div>
</template>

<script>
  import AppTable from '@/components/ui/AppTable'
  import BaseModal from '@/components/ui/Modal/BaseModal'
  import BarChart from '@/shared/ui/Charts/BarChart.vue'
  import ModalPrint from '@/components/ui/Modal/ModalPrint'
  import RadioGroup from '@/components/Form/Radio/RadioGroup'
  import ReportFilterInfo from '@/components/views/Analytics/ReportFilterInfo'
  import VButton from '@/components/simple/button/VButton'
  import VCheckbox from '@/components/Form/VCheckbox'
  import VSelect from '@/components/Form/VSelect'
  import ReclamationInnerTable from '@/components/views/Analytics/ReclamationInnerTable'
  import AnalyticsFilter from '@/components/views/Analytics/AnalyticsFilter'

  import { mixinModal } from '@/mixins/modal/mixinModal'
  import { mixinIsLoading } from '@/mixins/mixinIsLoading'

  import { getAll } from '@/api/request'
  import { mapGetters, mapState, mapMutations } from 'vuex'
  import {
    ANALYTICS_PRINT_TYPE_LIST,
    REPORTS_TYPE_DATA
  } from '@/const/analytics'
  import getValuesArray from '@/utils/array/getValuesArray'
  import { setAlertError } from '@/utils/store/alert'
  import { formatDate } from '@/utils/format/date'
  import { map2MockId } from '@/utils/array'
  import { getChartDatasets } from '@/utils/chart'
  import { mapReportWireConsumptionTableData } from '@/utils/map/mapReportWireConsumptionTableData'
  import { getHTML2canvasDataURL } from '@/utils/html2canvasLib'
  import { mapChartReportTypeDecision } from '@/utils/map/mapChartReportTypeDecision'
  import { sortTableItems, updateColumnSortState } from '@/utils/analytics'

  const LABELS = {
    application_area: 'Область применения',
    delay_reason: 'Причина просрочки',
    executor: 'Исполнитель',
    inner_reclamation_type: 'Причина рекламации',
    material: 'Марка стали',
    order_type: 'Тип заказа',
    responsible: 'Ответственный',
    stage: 'Этап',
    status: 'Этап',
    tech_manager: 'Ответственный',
    type: 'Тип пружины',
    spring_type: 'Тип пружины',
    winding_method: 'Способ навивки',
    workshop: 'Цех производства',
    report_type: 'Тип отчета',
    package_type: 'Вид тары',
    packers: 'Номер упаковщика',
    shape: 'Вид пружины',
    account_number: '№ заказа',
    is_inner_reclamation: 'Внутренние рекламации'
  }

  export default {
    name: 'AnalyticsView',
    components: {
      AnalyticsFilter,
      ReclamationInnerTable,
      AppTable,
      BaseModal,
      ReportFilterInfo,
      ModalPrint,
      RadioGroup,
      VButton,
      VCheckbox,
      BarChart,
      VSelect
    },
    mixins: [mixinModal, mixinIsLoading],
    data () {
      return {
        // Выбор отчета
        reportTypeList: REPORTS_TYPE_DATA,
        filters: {},
        perPage: 20,
        currentReport: null,
        isNotFound: false,

        // Таблица
        isOpenTableTree: false,
        isTable: true,
        items: [],
        columns: [],

        // График
        activeChartRadio: '',
        isChart: true,
        chartData: {
          labels: [],
          datasets: []
        },
        multiChartData: {},

        // Печать
        currentFilter: {},
        visibleReportFilterInfo: false,
        reportFilter: {
          date: '',
          filters: []
        },

        // Excel
        isLoadingExcel: false,
        excelHeader: [],

        modalTable: {
          items: []
        }
      }
    },

    computed: {
      ...mapState('users', {
        users: 'all'
      }),
      ...mapGetters('users', {
        executorList: 'executorList',
        stageResponsibleList: 'stageResponsibleList'
      }),
      ...mapGetters({
        propertiesAll: 'records/propertiesAll',
        shapeAll: 'records/shapeAll',
        getSelectedReport: 'records/getSelectedReport',
        isResetReport: 'records/isResetReport',
        getPackageTypes: 'records/getPackageTypes',
        getPackagePackers: 'records/getPackagePackers'
      }),

      url () {
        return 'analytics/' + this.currentReport.value
      },
      visibleChartRadio () {
        return this.currentReport.value === 'reclamation-inner'
      },

      printOptions () {
        if (!this.hasChart) {
          return ANALYTICS_PRINT_TYPE_LIST.slice(0, 1)
        }
        return ANALYTICS_PRINT_TYPE_LIST
      },

      excelFields () {
        let fields = {}
        let additionalFields = {}

        if (this.selectedReport?.excel?.handledAdditionalFieldsExcel) {
          additionalFields = this.selectedReport.excel.handledAdditionalFieldsExcel(this.currentFilter)
        }

        if (this.selectedReport?.excel?.handledFields) {
          this.columns.forEach(item => {
            const title = this.selectedReport.excel.handledFields(item.title, item.name)

            fields = {
              ...fields,
              [title]: item.name,
              ...(item.hasExtrasFieldsAfter ? additionalFields : [])
            }
          })
        } else {
          this.columns.forEach(item => {
            fields = {
              ...fields,
              [item.title]: item.name,
              ...(item.hasExtrasFieldsAfter ? additionalFields : [])
            }
          })
        }

        return fields
      },

      hasChart () {
        switch (this.selectedReport.value) {
          case 'overdue-orders':
          case 'stage':
          case 'wire-consumption':
          case 'time-at-work': return false
          case 'reclamation-inner-by-type': {
            return this.currentFilter?.report_type?.value !== 'loss'
          }
          default: return true
        }
      },

      pagesForReportStage () {
        return this.getCountPagesBy(this.sizeUsersForReportStage)
      },
      sizeUsersForReportStage () {
        return this.users.length
      },

      selectedReport: {
        get () {
          return this.getSelectedReport
        },
        set (data) {
          this.SET_SELECTED_REPORT(data)
          this.SET_IS_RESET_RECORD(false)
        }
      },
      isReportReclamationTypeCustomTable () {
        return this.isReclamationInnerReport
          && this.currentFilter.report_type.value !== 'loss'
          && this.currentFilter.report_type.value !== 'stats'
      },
      isReclamationInnerReport () {
        return this.selectedReport.value === 'reclamation-inner-by-type'
      },
      isPackagesReport () {
        return this.selectedReport.value === 'packages'
      }
    },

    watch: {
      isTable () {
        if (!this.isTable) {
          this.isChart = true
        }
      },
      isChart () {
        if (!this.isChart) {
          this.isTable = true
        }
      },
      isResetReport (val) {
        if (val) this.changeReport(this.selectedReport)
      },
      selectedReport (data) {
        this.changeReport(data)
      }
    },

    mounted () {
      this.changeReport(this.selectedReport)
    },

    beforeDestroy () {
      this.filters = {}
      this.items = []
      this.columns = []

      this.resetChartData()
    },

    methods: {
      ...mapMutations({
        SET_SELECTED_REPORT: 'records/SET_SELECTED_REPORT',
        SET_IS_RESET_RECORD: 'records/SET_IS_RESET_RECORD',
        SET_INFO_SELECTED_REPORT: 'records/SET_INFO_SELECTED_REPORT'
      }),

      changeReport (data) {
        this.isNotFound = false
        this.resetFilter()
        this.items = []
        this.columns = []
        this.resetChartData()
        this.resetReportFilterInfo()

        this.isChart = this.hasChart

        this.filters = { ...data.filters }
        this.columns = [...data.table.columns]

        this.currentReport = data
      },

      resetFilter () {
        this.filters = {}
        this.currentFilter = {}
        if (this.$refs.analyticsFilter) {
          this.$refs.analyticsFilter.clear()
        }
      },

      async filterSubmit (params, originalParams) {
        this.isNotFound = false
        this.currentFilter = { ...originalParams }

        await this.loadReport(params)
        this.SET_IS_RESET_RECORD(false)
      },

      async loadReport (params) {
        this.startLoading()
        this.isNotFound = false
        this.items = []
        this.resetChartData()

        try {
          const res = this.currentReport.value === 'stage' ?
            await this.getReportStage(params)
            : await getAll(this.url, params)

          const hasItems = res.data?.items?.length || res.data?.table?.length || res.data?.length || res.data?.rows?.length
          if (!hasItems) {
            this.isNotFound = true
            return
          }

          switch (this.currentReport.value) {
            case 'wire-consumption': {
              this.items = mapReportWireConsumptionTableData(res.data.items)
              break
            }
            case 'reclamation-inner-by-type': {
              this.changeInfoForType()
              this.items = res.data
              if (this.currentFilter.report_type.value === 'loss') {
                this.items = mapReportWireConsumptionTableData(this.items)
                this.isChart = false
              }

              if (this.currentReport?.chart) {
                const chartLabels = getValuesArray(this.items, this.currentReport.chart.labels) || []
                this.chartData.labels = this.currentFilter.report_type.value === 'type' ? mapChartReportTypeDecision(chartLabels) : chartLabels

                this.chartData.datasets = getChartDatasets(this.items, this.currentReport.chart.datasets)
              }
              break
            }
            case 'packages': {
              this.items = res.data

              if (this.currentReport?.chart) {
                this.chartData.labels = getValuesArray(this.items, this.currentReport.chart.labels) || []
                this.chartData.datasets = getChartDatasets(this.items, this.currentReport.chart.datasets)
              }
              break
            }
            case 'time-at-work': {
              this.changeInfoForType()
              this.items = res.data.rows

              break
            }
            default: {
              this.items = res.data.items

              if (this.currentReport?.chart) {
                this.chartData.labels = getValuesArray(this.items, this.currentReport.chart.labels) || []
                this.chartData.datasets = getChartDatasets(this.items, this.currentReport.chart.datasets)
              }
            }
          }
        } catch (e) {
          await setAlertError(e, 'Не удалось загрузить отчет')
        } finally {
          this.finishLoading()
        }
      },
      async getReportStage (params) {
        const pages = this.getCurrentPages(params)

        const arrayForMap = Array.from({ length: pages })

        const response = await Promise.all(arrayForMap.map((_, i) => getAll(this.url, {
          ...params,
          page: i + 1,
          'per-page': this.perPage
        })))

        const mergedResponse = {
          data: {
            items: []
          }
        }

        response.forEach(res => {
          mergedResponse.data.items.push(...res.data.items)
        })

        return mergedResponse
      },

      getCurrentPages (params) {
        const userIdsSelected = [
          ...(params?.executor_ids || []),
          ...(params?.responsible_ids || [])
        ]

        if (userIdsSelected.length) {
          const size = this.getCountUsersBy(userIdsSelected)
          return this.getCountPagesBy(size)
        }

        return this.pagesForReportStage
      },

      getCountUsersBy (ids) {
        const setIds = new Set(ids)
        return setIds.size
      },
      getCountPagesBy (size) {
        const count = size / this.perPage
        return Math.ceil(count)
      },

      changeActiveChartRadio (value) {
        this.activeChartRadio = value

        switch (value) {
          case 'date': {
            let labels = []
            let data = []
            for (const year in this.multiChartData.date) {
              labels = [...labels, ...Object.keys(this.multiChartData.date[year]).map(key => `${key}.${year}`)]
              data = [...data, ...Object.values(this.multiChartData.date[year])]
            }

            this.chartData.labels = labels || []
            this.chartData.datasets[0].data = data || []
            break
          }
          case 'reason': {
            this.chartData.labels = Object.keys(this.multiChartData.reason) || []
            this.chartData.datasets[0].data = Object.values(this.multiChartData.reason) || []
            break
          }
          case 'responsible': {
            this.chartData.labels = Object.keys(this.multiChartData.person) || []
            this.chartData.datasets[0].data = Object.values(this.multiChartData.person) || []
          }
        }
      },

      async toPrint (data) {
        try {
          const type = data.type
          this.$store.commit('SET_IS_SENDING_REQUEST_MODAL')

          this.setReportFilterInfo()
          this.toggleVisibleFilterInfo()
          this.toggleIsOpenTableTree()

          // Чтобы при печати у таблицы не было скролла
          if (this.$refs?.table?.$el?.style) {
            this.$refs.table.$el.style.maxHeight = '100%'
            this.$refs.table.$el.style.overflowY = 'visible'
          }

          if (type === 'table') {
            await this.printTable()
          }

          if (type === 'chart') {
            await this.printChart()
          }
        } catch (e) {
          await setAlertError(e, 'Не удалось подготовить данные для печати')
        } finally {
          this.toggleVisibleFilterInfo()
          this.toggleIsOpenTableTree()
          if (this.$refs?.table?.$el?.style) {
            this.$refs.table.$el.style.maxHeight = ''
            this.$refs.table.$el.style.overflowY = ''
          }
          this.$store.commit('SET_IS_SENDING_REQUEST_MODAL', false)
          this.closeModal()
        }
      },
      async printChart () {
        const element = window.document.getElementById('printChart')

        const src = await getHTML2canvasDataURL(element)

        try {
          const win = window.open()
          await win.document.write(`<br><img style="max-width: 100vw" src="${src}"/>`)
          win.print()
        } catch (e) {
          await setAlertError(e, 'Всплывающее окно заблокировано браузером')
        }
      },
      async printTable () {
        return new Promise(resolve => {
          setTimeout(() => {
            this.$htmlToPaper('printTable')
            resolve()
          }, 0)
        })
      },
      toggleVisibleFilterInfo () {
        this.visibleReportFilterInfo = !this.visibleReportFilterInfo
      },
      toggleIsOpenTableTree () {
        this.isOpenTableTree = !this.isOpenTableTree
      },

      resetChartData () {
        this.chartData.labels = []
        this.chartData.datasets = []
        this.multiChartData = {}
      },

      async getExcelData () {
        try {
          this.isLoadingExcel = true
          await this.$store.dispatch('alert/setAlert', { type: 'warning', message: 'Началась подготовка отчета, пожалуйста, подождите' })

          this.setExcelHeader()

          if (this.isReportReclamationTypeCustomTable) {
            return this.selectedReport.excel.getMapped(this.items)
          }

          const totalItem = {
            [this.columns[0].name]: 'Итого',
            ...this.$refs.table?.$refs?.total?.totalItem
          }

          if (this.selectedReport?.excel?.handlerRows) {
            const rows = this.selectedReport.excel.handlerRows(this.items)
            return [...rows, totalItem]
          }

          return [...this.items, totalItem]
        } catch (error) {
          await setAlertError(error, 'Не удалось подготовить данные для экспорта')
        } finally {
          this.isLoadingExcel = false
        }
      },
      setExcelHeader () {
        this.setReportFilterInfo()

        const headers = [this.selectedReport.name, ' ']
        if (this.reportFilter.date) {
          headers.push(this.reportFilter.date, ' ')
        }

        const filterTexts = this.reportFilter.filters.map(item => {
          const values = Array.isArray(item.text) ? item.text.join('; ') : item.text
          return `${item.label}: ${values}`
        })

        if (filterTexts.length) {
          headers.push(...filterTexts, ' ')
        }

        this.excelHeader = headers
      },

      // Установка фильтра для печати
      setReportFilterInfo () {
        this.reportFilter.date = this.getReportDates()

        const excludedFields = ['date_start', 'date_finish']
        const filters = Object.entries(this.currentFilter)
          .filter(([key, value]) => !excludedFields.includes(key) && value)
          .map(([key, value]) => ({
            label: LABELS[key],
            text: this.getFilterText(key)
          }))
        this.reportFilter.filters = map2MockId(filters)
      },
      getFilterText (filterName) {
        switch (filterName) {
          case 'report_type':
          case 'is_inner_reclamation': return [this.currentFilter[filterName].name]
          case 'packers': return getValuesArray(this.currentFilter[filterName], 'packer')
          default: return getValuesArray(this.currentFilter[filterName], 'name')
        }
      },
      getReportDates () {
        const dateStart = this.currentFilter?.date_start
        if (!dateStart) return ''

        const start = formatDate(dateStart)
        const finish = this.currentFilter.date_finish !== dateStart ? ` - ${formatDate(this.currentFilter.date_finish)}` : ''
        return start + finish
      },
      resetReportFilterInfo () {
        this.reportFilter.date = ''
        this.reportFilter.filters = []
      },

      onTableRow (item) {
        if (this.selectedReport.modal) {
            this.modalTable.items.push(item)
            this.openModal('table')
        }
      },
      closeModalTable () {
        this.closeModal()
        this.modalTable.items.length = 0
      },
      changeInfoForType () {
        if (!this.currentFilter?.report_type) return
        const table = this.currentFilter.report_type?.table
        const chart = this.currentFilter.report_type?.chart
        const excel = this.currentFilter.report_type?.excel

        this.columns = [...table.columns]
        this.SET_INFO_SELECTED_REPORT({ table, chart, excel })
      },

      // Сортировка таблицы по полям
      sortedHandler (data) {
        // Сбрасываем сортировку у других полей при изменении
        const updatedColumns = updateColumnSortState(
          this.selectedReport.table.columnsBefore || [],
          this.columns,
          data.name,
          !data.sortDesc
        )

        // Обновляем columnsBefore в конфигурации отчета
        if (this.selectedReport.table.columnsBefore) this.selectedReport.table.columnsBefore = updatedColumns.columnsBefore
        this.columns = updatedColumns.columns

        // Сортируем данные
        this.items = sortTableItems(this.items, {
          ...data,
          sortDesc: !data.sortDesc
        })
      }
    }
  }
</script>
